import { BusinessModel, OrganizationApplicationParameters } from '@platform/client/schemas';
import { FormsMultilineInput, RadioGroup } from '@platform/components';
import { SubmitHandler, useForm } from 'react-hook-form';
import { ApplicationPageProps } from '../../Application';
import { useApplicationLocalStorage } from '../../application-hook';
import { ApplicationBackground } from '../../ApplicationBackground';
import { ApplicationPageContent } from '../../components/ApplicationPageContent';
import { OrgBusinessModelShowcase } from './OrgBusinessModelShowcase';

type FormValues = {
  businessModel: BusinessModel;
  businessModelDescription?: string;
};

export const OrgBusinessModelSelection = (props: ApplicationPageProps) => {
  const { onBack, onContinue, progress, dataKey } = { ...props };

  const { updateApplicationData, getApplicationDataByKey } =
    useApplicationLocalStorage<OrganizationApplicationParameters>(dataKey);

  const {
    register,
    control,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
  } = useForm<FormValues>({
    mode: 'onBlur',
    reValidateMode: 'onChange',
    defaultValues: {
      businessModel: getApplicationDataByKey('businessModel'),
      businessModelDescription: getApplicationDataByKey('businessModelDescription') || '',
    },
  });

  const onChange = (value?: string | number) => {
    setValue('businessModel', value as BusinessModel);
  };

  const submit: SubmitHandler<FormValues> = (data) => {
    updateApplicationData({
      businessModel: data.businessModel,
      businessModelDescription: data.businessModelDescription,
    });
    onContinue();
  };

  return (
    <ApplicationBackground
      progress={progress}
      isOrganization={true}
      onBack={onBack}
      onSubmit={handleSubmit(submit)}
      customLeftPanelContent={<OrgBusinessModelShowcase />}
      isEditProfile={props.isEditProfile}
    >
      <ApplicationPageContent
        title="Which of the following best describes your organization's business model?"
        description={
          'This helps us recommend opportunities and support most aligned with how you operate.'
        }
      >
        <RadioGroup
          name='business-model-category-selection'
          aria-label='business-model-category-selection'
          register={() => {
            return register('businessModel', {
              required: 'Select your business model to continue',
            });
          }}
          error={errors.businessModel?.message}
          items={[
            {
              title: 'Business-to-Business (B2B) - we sell to other businesses',
              value: BusinessModel.B2B,
            },
            {
              value: BusinessModel.B2C,
              title: 'Business-to-Consumer (B2C) - we sell directly to individual consumers',
            },
            {
              value: BusinessModel['B2B-and-B2C'],
              title: 'Both B2B and B2C',
            },
            {
              value: BusinessModel.Other,
              title: 'Other',
            },
          ]}
          defaultValue={getApplicationDataByKey('businessModel')}
          onChange={onChange}
        />
        {watch('businessModel') === 'Other' && (
          <FormsMultilineInput
            label="Tell us more about your organization's business model"
            placeholder='Enter a description...'
            rules={{
              maxLength: 500,
              required: "Tell us more about your organization's business model to continue",
            }}
            characterCount={500}
            control={control}
            name='businessModelDescription'
            formsError={errors.businessModelDescription}
          />
        )}
      </ApplicationPageContent>
    </ApplicationBackground>
  );
};
